import React, { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../../Config";
import { AuthIcons, Icon, LanguagePill } from "../AuthLayout";
import { StatusBadge } from "../ResetPassword/shared";
import { setAllClear, startSession } from "../../../store/Actions/authActions";
import { clearCart, setClearCartState } from "../../../store/Actions/cartActions";
import { claimBody, claimFailure, claimFormErrors, greeting, hasErrors, lookupState } from "../../../utils/claim";
import "../ResetPassword/account.css";
import "./claim.css";

/**
 * /c/:token — un client créé au comptoir de la blanchisserie active son
 * accès en ligne sur son compte existant (même historique de commandes).
 * Le lien arrive par mail, SMS ou QR depuis le POS ; sur téléphone l'app
 * le prend, sur ordinateur on arrive ici. Règles et réponses du backend :
 * src/utils/claim.js.
 *
 * Aux couleurs de la blanchisserie : WOSH n'apparaît qu'une fois, en pied
 * de page, comme prestataire du suivi.
 */

const APP_STORE = "https://apps.apple.com/be/app/wosh/id1612458076";
const GOOGLE_PLAY = "https://play.google.com/store/apps/details?id=com.woshuser";

const Page = ({ t, shop, children }) => (
  <div className="wosh-auth wosh-claim">
    <AuthIcons />
    <header className="wosh-claim__top">
      <span className="wosh-claim__shop">
        {shop && (
          <>
            <span className="wosh-claim__shop-icon">
              <Icon name="i-store" />
            </span>
            {shop}
          </>
        )}
      </span>
      <LanguagePill />
    </header>
    <main className="wosh-claim__body">{children}</main>
    <footer className="wosh-claim__foot">{t("Order tracking by WOSH")}</footer>
  </div>
);

function ClaimForm({ t, account, submitting, fieldErrors, alert, onSubmit }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [shown, setShown] = useState(false);
  const [attempted, setAttempted] = useState(false);
  /* Une erreur du serveur s'efface dès que le champ est modifié. */
  const [cleared, setCleared] = useState({});
  useEffect(() => setCleared({}), [fieldErrors]);

  const local = claimFormErrors({ needsEmail: account.needsEmail, email, password, confirmation });
  const error = (key) => {
    if (attempted && local[key]) return t(local[key]);
    if (fieldErrors?.[key] && !cleared[key]) return t(fieldErrors[key]);
    return null;
  };
  const edit = (key, setter) => (event) => {
    setter(event.target.value);
    setCleared((previous) => ({ ...previous, [key]: true }));
  };

  const submit = (event) => {
    event.preventDefault();
    setAttempted(true);
    if (hasErrors(local) || submitting) return;
    onSubmit({ email, password });
  };

  const passwordField = (id, label, value, key, hint) => (
    <>
      <label className="wosh-auth__label" htmlFor={id}>
        {label}
      </label>
      {hint && <p className="wosh-claim__hint">{hint}</p>}
      <div className={`wosh-auth__field${error(key) ? " wosh-auth__field--invalid" : ""}`}>
        <Icon name="i-lock" />
        <input
          id={id}
          type={shown ? "text" : "password"}
          autoComplete="new-password"
          placeholder="••••••••"
          value={value}
          onChange={edit(key, key === "password" ? setPassword : setConfirmation)}
        />
        <button
          type="button"
          className={`wosh-auth__reveal${shown ? " wosh-auth__reveal--on" : ""}`}
          onClick={() => setShown(!shown)}
          aria-label={t("Show/Hide Password")}
        >
          <Icon name="i-eye" />
        </button>
      </div>
      {error(key) && <p className="wosh-auth__error">{error(key)}</p>}
    </>
  );

  return (
    <form onSubmit={submit} noValidate>
      <label className="wosh-auth__label" htmlFor="claimEmail">
        {t("Email address")}
      </label>
      {account.needsEmail ? (
        <>
          <p className="wosh-claim__hint">{t("You will use it to sign in and receive your order updates.")}</p>
          <div className={`wosh-auth__field${error("email") ? " wosh-auth__field--invalid" : ""}`}>
            <Icon name="i-mail" />
            <input
              id="claimEmail"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t("name@example.com")}
              value={email}
              onChange={edit("email", setEmail)}
            />
          </div>
          {error("email") && <p className="wosh-auth__error">{error("email")}</p>}
        </>
      ) : (
        /* Lecture seule, mais un vrai champ : le gestionnaire de mots de
           passe l'enregistre comme identifiant. */
        <div className="wosh-auth__field wosh-claim__readonly">
          <Icon name="i-mail" />
          <input id="claimEmail" type="email" autoComplete="username" value={account.email} readOnly />
        </div>
      )}
      <div className="wosh-acc__gap" />
      {passwordField("claimPassword", t("Password"), password, "password", t("At least 6 characters."))}
      <div className="wosh-acc__gap" />
      {passwordField("claimConfirmation", t("Confirm the password"), confirmation, "confirmation")}
      {alert && <div className="wosh-auth__alert wosh-claim__alert">{t(alert)}</div>}
      <button type="submit" className="wosh-auth__cta wosh-acc__cta" disabled={submitting}>
        {submitting && <span className="wosh-auth__spinner" />}
        {submitting ? t("processing") : t("Activate my account")}
      </button>
    </form>
  );
}

export function AccountClaim({ token, dispatch }) {
  const { t } = useTranslation();
  const [page, setPage] = useState({ state: "loading" });
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState(null);
  const [alert, setAlert] = useState(null);
  /* Garde synchrone : deux clics dans le même instant ne font qu'une requête
     (la seconde recevrait un 404 et ressemblerait à un échec). */
  const inFlight = useRef(false);
  /* Une requête restée sans réponse a peut-être abouti : un 404 ensuite veut
     alors dire « déjà activé », pas « lien expiré ». */
  const uncertain = useRef(false);

  const lookup = useCallback(() => {
    setPage({ state: "loading" });
    Axios.post(`${Config.apiURL}/claim/lookup`, { token }, Config.headers)
      .then((response) => setPage(lookupState(response)))
      .catch((error) => setPage(lookupState(null, error)));
  }, [token]);

  useEffect(() => {
    if (token) lookup();
    else setPage({ state: "invalid" });
  }, [token, lookup]);

  const account = page.account;

  const submit = async ({ email, password }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors(null);
    setAlert(null);
    let response;
    try {
      response = await Axios.post(
        `${Config.apiURL}/claim`,
        claimBody({ token, needsEmail: account.needsEmail, email, password }),
        Config.headers
      );
    } catch (error) {
      const failure = claimFailure(error);
      if (!error?.response) uncertain.current = true;
      if (failure.state === "invalid" && uncertain.current) {
        setPage({ ...page, state: "claimed" });
      } else if (failure.state) {
        setPage({ ...page, state: failure.state, existingEmail: failure.existingEmail });
      } else {
        setFieldErrors(failure.fields || null);
        setAlert(failure.alert || null);
      }
      inFlight.current = false;
      setSubmitting(false);
      return;
    }

    /* Comme la connexion, après avoir vidé la session d'un éventuel autre
       utilisateur de ce navigateur. */
    localStorage.clear();
    dispatch(setAllClear());
    dispatch(setClearCartState());
    dispatch(clearCart());
    let signedIn = true;
    try {
      await dispatch(startSession(response));
    } catch (error) {
      signedIn = false;
    }
    const addedEmail = account.needsEmail ? claimBody({ token, needsEmail: true, email }).email : "";
    setPage({ ...page, state: "done", signedIn, addedEmail });
    setSubmitting(false);
  };

  const shop = account?.laundryName;

  if (page.state === "loading") {
    return (
      <Page t={t}>
        <div className="wosh-claim__loading">
          <span className="wosh-auth__spinner" aria-busy="true" />
        </div>
      </Page>
    );
  }

  if (page.state === "error") {
    return (
      <Page t={t}>
        <StatusBadge icon="i-time" tone="warning" />
        <h1 className="wosh-auth__title">{t("We couldn't open this link")}</h1>
        <p className="wosh-auth__lede">{t("Check your connection and try again.")}</p>
        <button type="button" className="wosh-auth__cta wosh-acc__cta" onClick={lookup}>
          {t("Try again")}
        </button>
      </Page>
    );
  }

  if (page.state === "invalid") {
    return (
      <Page t={t} shop={shop}>
        <StatusBadge icon="i-time" tone="brand" />
        <h1 className="wosh-auth__title">{t("This link is no longer valid")}</h1>
        <p className="wosh-auth__lede">
          {t("This link has expired or was already used. The shop can send you a new one.")}
        </p>
        <p className="wosh-auth__switch">
          {t("Already activated your account?")} <Link to="/sign-in">{t("Sign in")}</Link>
        </p>
      </Page>
    );
  }

  if (page.state === "claimed") {
    return (
      <Page t={t} shop={shop}>
        <StatusBadge icon="i-check" tone="success" />
        <h1 className="wosh-auth__title">{t("Your account is already active")}</h1>
        <p className="wosh-auth__lede">{t("Sign in with your email address and password.")}</p>
        <Link to="/sign-in" className="wosh-auth__cta wosh-acc__cta">
          {t("Sign in")}
        </Link>
        <p className="wosh-auth__switch">
          <Link to="/forgot-password">{t("Forgot your password?")}</Link>
        </p>
      </Page>
    );
  }

  if (page.state === "exists") {
    return (
      <Page t={t} shop={shop}>
        <StatusBadge icon="i-lock" tone="warning" />
        <h1 className="wosh-auth__title">{t("You already have an online account")}</h1>
        <p className="wosh-auth__lede">
          {page.existingEmail ? (
            <>
              {t("An account with your phone number already exists:")}{" "}
              <span className="wosh-acc__email">{page.existingEmail}</span>.{" "}
            </>
          ) : (
            <>{t("An account with your phone number already exists.")} </>
          )}
          {shop
            ? t("Contact {{laundry}} so they can link your orders to it.", { laundry: shop })
            : t("Contact the shop so they can link your orders to it.")}
        </p>
      </Page>
    );
  }

  if (page.state === "done") {
    return (
      <Page t={t} shop={shop}>
        <StatusBadge icon="i-check" tone="success" />
        <h1 className="wosh-auth__title">{t("Your account is active")}</h1>
        <p className="wosh-auth__lede">
          {page.signedIn
            ? t("You can now follow your orders online.")
            : t("Sign in with your email address and your new password.")}
          {page.addedEmail && (
            <>
              {" "}
              {t("Your email address has been saved:")} <span className="wosh-acc__email">{page.addedEmail}</span>
            </>
          )}
        </p>
        <Link
          to={page.signedIn ? "/orders?tab=past" : "/sign-in"}
          className="wosh-auth__cta wosh-acc__cta"
        >
          {page.signedIn ? t("See my orders") : t("Sign in")}
        </Link>
        <div className="wosh-claim__apps">
          <p>{t("Follow your orders on your phone too:")}</p>
          <div className="wosh-claim__badges">
            <a href={APP_STORE} target="_blank" rel="noreferrer">
              <img src="/assets/imgs/appStore.png" alt={t("Download on the App Store")} />
            </a>
            <a href={GOOGLE_PLAY} target="_blank" rel="noreferrer">
              <img src="/assets/imgs/googlePlay.png" alt={t("Get it on Google Play")} />
            </a>
          </div>
        </div>
      </Page>
    );
  }

  const hello = greeting(account);
  return (
    <Page t={t} shop={shop}>
      <h1 className="wosh-auth__title">{t(...hello.title)}</h1>
      <p className="wosh-auth__lede">
        {t(...hello.lede)} {t("Choose a password to follow your orders online.")}
      </p>
      <ClaimForm
        t={t}
        account={account}
        submitting={submitting}
        fieldErrors={fieldErrors}
        alert={alert}
        onSubmit={submit}
      />
    </Page>
  );
}

const ClaimRoute = ({ dispatch }) => {
  const { token } = useParams();
  return <AccountClaim token={token} dispatch={dispatch} />;
};

export default connect()(ClaimRoute);
